import {
  HHAvatar,
  HHDivider,
  HHIconButton,
  HHMenu,
  HHMenuItem,
  HHStack,
  HHTooltip,
  HHTypography,
  useHingeHealthSecurityContext,
} from '@hinge-health/react-component-library';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { MouseEvent, useState } from 'react';
import { OktaLocalStorageKeys } from '../constants/okta';
import { menuTheme } from './menu.theme';

const UserProfileMenu = (): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { hingeHealthAuthState, hingeHealthAuthClient } =
    useHingeHealthSecurityContext();

  const claims = hingeHealthAuthState?.accessToken?.claims;
  const email = claims?.email ?? '';
  const uid = (claims?.uid as string) ?? '';

  const handleOpen = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleCopyUid = async (): Promise<void> => {
    await navigator.clipboard.writeText(uid);
  };

  const handleLogout = async (): Promise<void> => {
    const allOktaKeys = Object.values(OktaLocalStorageKeys);
    try {
      await hingeHealthAuthClient.signOut();
    } finally {
      allOktaKeys.forEach((key: string) => localStorage.removeItem(key));
    }
  };

  return (
    <>
      <HHTooltip hhVariant="variant-bypass" title="Profile" placement="right">
        <HHIconButton
          hhVariant="variant-bypass"
          onClick={handleOpen}
          size="small"
          aria-label="Open user profile menu"
        >
          <HHAvatar
            hhVariant="variant-bypass"
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'white',
              color: menuTheme.colors.menu,
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            <PersonIcon sx={{ fontSize: 20 }} />
          </HHAvatar>
        </HHIconButton>
      </HHTooltip>

      <HHMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        PaperProps={{
          sx: { minWidth: 260 },
        }}
      >
        <HHStack sx={{ px: 2, py: 1.5, outline: 'none' }}>
          <HHTypography
            hhVariant="body1"
            sx={{ fontWeight: 600, wordBreak: 'break-all' }}
          >
            {email}
          </HHTypography>
          <HHStack
            direction="row"
            alignItems="center"
            sx={{ gap: 0.5, mt: 0.75 }}
          >
            <HHTypography
              hhVariant="caption"
              sx={{
                color: 'text.secondary',
                fontFamily: 'monospace',
                flexShrink: 1,
                wordBreak: 'break-all',
              }}
            >
              {uid}
            </HHTypography>
            <HHIconButton
              hhVariant="variant-bypass"
              size="small"
              onClick={handleCopyUid}
              aria-label="Copy user ID"
              sx={{ flexShrink: 0 }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </HHIconButton>
          </HHStack>
        </HHStack>

        <HHDivider />

        <HHMenuItem sx={{ px: 2 }} onClick={handleLogout} disableGutters>
          <LogoutIcon sx={{ mr: 1, fontSize: 18 }} />
          <HHTypography hhVariant="body1">Log Out</HHTypography>
        </HHMenuItem>
      </HHMenu>
    </>
  );
};

export default UserProfileMenu;
